import React, { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import Modal from "../Model";
import RecordPaymentForm from "./RecordPaymentForm";
import { formatRupees } from "./paymentQueries";

const GET_SUGGESTIONS = gql`
  query getBillSettlementSuggestions {
    getBillSettlementSuggestions {
      dealerName
      date
      paymentMethod
      amount
      bills {
        id
        amount
        billDate
      }
    }
  }
`;

// Same dealer, day and method can still be separate payments, so nothing here
// links until an admin confirms it against their payment records.
const SettlementSuggestions = ({ canLink, onLinked }) => {
  const { data, refetch } = useQuery(GET_SUGGESTIONS, {
    fetchPolicy: "network-only",
  });
  const [isOpen, setIsOpen] = useState(false);
  const [linking, setLinking] = useState(null);

  const suggestions = data?.getBillSettlementSuggestions || [];

  if (!canLink || suggestions.length === 0) {
    return null;
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="px-5 py-2 border border-[#FBB033] text-[#FBB033] rounded-md font-semibold whitespace-nowrap hover:bg-[#FBB033] hover:text-black transition"
      >
        Suggested Links ({suggestions.length})
      </button>

      {isOpen && !linking && (
        <Modal
          onClose={() => setIsOpen(false)}
          disableOutsideClick={true}
          widthClass="sm:w-[700px]"
        >
          <div className="flex flex-col space-y-4 text-gray-800">
            <div className="flex justify-between border-b pb-3">
              <h3 className="text-xl font-semibold text-[#FBB033]">
                Suggested Links
              </h3>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-gray-500"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-500">
              These paid bills share a dealer, payment date and method. Link
              them only if they were one payment.
            </p>
            {suggestions.map((group) => (
              <div
                key={group.bills.map((bill) => bill.id).join(",")}
                className="flex items-center justify-between gap-4 rounded-md border border-gray-200 p-3 text-sm"
              >
                <div>
                  <p className="font-semibold">{group.dealerName}</p>
                  <p className="text-gray-500">
                    {new Date(group.date).toLocaleDateString("en-IN")} ·{" "}
                    {group.paymentMethod || "-"} · {formatRupees(group.amount)}
                  </p>
                  <p className="text-gray-500">
                    {group.bills
                      .map((bill) => formatRupees(bill.amount))
                      .join(" + ")}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setLinking(group)}
                  className="bg-[#FBB033] px-4 py-2 rounded-md font-semibold text-black"
                >
                  Link
                </button>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {linking && (
        <Modal onClose={() => setLinking(null)} disableOutsideClick={true}>
          <RecordPaymentForm
            bills={linking.bills}
            dealerName={linking.dealerName}
            initialDate={linking.date}
            initialMethod={linking.paymentMethod}
            onClose={() => setLinking(null)}
            onSuccess={() => {
              refetch();
              onLinked?.();
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default SettlementSuggestions;
